/**
 * Confirmed Demos walkthrough session registry + demo decision service.
 *
 * Shared review controls (TaskApprovalSection, Media Theater) consult
 * `demoDecisionRouteFor` ONLY for rows that carry a synthetic walkthrough
 * persona identity. When no confirmed session covers the exact task, the
 * function returns null and production routing is unchanged.
 *
 * No database change: uses the architect's existing update authority (see
 * decideDemoWalkthroughApproval). The repo-only SQL candidate in
 * docs/migrations/demo-walkthrough-decision.sql is a fallback, not used.
 * Evidence from this path covers synthetic rows + operator authority only,
 * never real-member authorization.
 */
import { supabase } from '@/integrations/supabase/client';
import { isOwnedReviewId, DEMO_REVIEW_COLUMNS } from './demoPersistence';
import type { ApprovalDecision, DecisionResult, DecidedApproval } from '@/lib/approvals/internalApprovalDecision';

export const DEMOS_INSTANCE = '87273678-64d6-41b4-961c-7357d2631c75';
export const DEMOS_PROJECT = '213ec846-5e2c-420b-871b-2dce09aa4a53';
export const DEMOS_SPACE = '3b68785e-254e-458a-9e9b-9f4093d929c9';

const SLOTS = ['owner', 'admin', 'manager', 'user_1', 'user_2', 'visitor'] as const;
export type PersonaSlot = typeof SLOTS[number];

export const demoPersonaEmail = (slot: string) => `demo-${slot}@walkthrough.workitect.invalid`;
const EMAIL_RE = /^demo-(owner|admin|manager|user_1|user_2|visitor)@walkthrough\.workitect\.invalid$/;

/** Slot of a synthetic walkthrough row, or null for any real/other row. */
export function syntheticPersonaSlot(row: { approver_type?: string | null; approver_user_id?: string | null; approver_email?: string | null }): PersonaSlot | null {
  if (row.approver_type !== 'email' || row.approver_user_id) return null;
  const m = EMAIL_RE.exec(row.approver_email ?? '');
  return m ? (m[1] as PersonaSlot) : null;
}

export interface ConfirmedDemoSession {
  sessionKey: string;
  instanceId: string;
  projectId: string;
  spaceId: string;
  taskIds: ReadonlySet<string>;
}

let active: ConfirmedDemoSession | null = null;

/** The one review step currently open: exact task + reviewer slot + confirmed viewing persona. */
export interface BoundDemoCheckpoint { taskId: string; reviewerSlot: PersonaSlot; activeSlot: PersonaSlot | null }
let bound: BoundDemoCheckpoint | null = null;
/** Bumped on every session/checkpoint change; decisions started under an older value never report success. */
let generation = 0;
const listeners = new Set<() => void>();
const emit = () => { generation++; for (const l of listeners) l(); };
export const subscribeDemoDecisions = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const demoDecisionGeneration = () => generation;

/** Set only by DemoContext after exact targets are resolved and the snapshot is saved. */
export function setConfirmedDemoSession(s: ConfirmedDemoSession | null) {
  bound = null;
  editsOpen = false;
  if (s && (s.instanceId !== DEMOS_INSTANCE || s.projectId !== DEMOS_PROJECT || s.spaceId !== DEMOS_SPACE)) {
    active = null;
  } else {
    active = s;
  }
  emit();
}
export const getConfirmedDemoSession = () => active;

/**
 * Bind (or clear, with null) the open review step. DemoContext clears this
 * synchronously the moment any transition or exit starts, so eligibility is
 * withdrawn before any baseline/restore write.
 */
export function bindDemoCheckpoint(cp: BoundDemoCheckpoint | null) {
  if (cp && (!active || !active.taskIds.has(cp.taskId) || !SLOTS.includes(cp.reviewerSlot))) cp = null;
  bound = cp;
  emit();
}
export const getBoundDemoCheckpoint = () => bound;

/**
 * The persona actually shown in Sandbox right now, or null (own account, not a
 * walkthrough persona, or name mismatch). Never inferred from the chapter.
 */
export function liveDemoSlot(
  sb: { isActive: boolean; sandboxSlotKey: string | null; impersonatedLabel: string | null },
  personas: Partial<Record<string, { name: string }>> | null | undefined,
): PersonaSlot | null {
  if (!sb.isActive || !sb.sandboxSlotKey || !SLOTS.includes(sb.sandboxSlotKey as PersonaSlot)) return null;
  const p = personas?.[sb.sandboxSlotKey];
  return p && p.name === sb.impersonatedLabel ? (sb.sandboxSlotKey as PersonaSlot) : null;
}

/**
 * Re-bind the open review step to the live Sandbox persona. A change replaces
 * the bound object, so any decision started under the old persona fails as
 * stale before its conditional write.
 */
export function syncDemoActiveSlot(slot: PersonaSlot | null) {
  if (bound && bound.activeSlot !== slot) bindDemoCheckpoint({ ...bound, activeSlot: slot });
}

// In-flight decision writes; the coordinator drains these before baseline/restore.
const inflight = new Set<Promise<unknown>>();
export async function drainDemoDecisions(): Promise<void> {
  while (inflight.size) await Promise.allSettled([...inflight]);
}
const track = <T,>(p: Promise<T>): Promise<T> => {
  inflight.add(p);
  p.finally(() => inflight.delete(p)).catch(() => undefined);
  return p;
};

/**
 * Interactive task-write gate for walkthrough scenario tasks. DemoContext opens
 * it only while the walkthrough is resting on a chapter (idle/awaiting) and
 * closes it synchronously when any transition or exit starts.
 */
let editsOpen = false;
export function setDemoEditsOpen(open: boolean) { editsOpen = open; }

/**
 * Bounded adapter around the shared task writer. Rows outside a confirmed
 * walkthrough's manifest run unchanged (production). Scenario-task writes are
 * refused once a transition/exit has started; accepted ones are tracked so the
 * coordinator drains them before any baseline reset or restore.
 */
/**
 * Scenario date/reschedule changes are refused for the whole confirmed
 * walkthrough (restore cannot be a verified canonical schedule rollback).
 * Tasks outside the manifest, and all tasks with no session, are unaffected.
 */
export function isDemoScenarioRescheduleRefused(taskId: string): boolean {
  return !!active && active.taskIds.has(taskId);
}

/** Wrap the shared reschedule writer: scenario tasks refused (no write) during a confirmed walkthrough. */
export function gateDemoReschedule<A extends unknown[]>(
  write: (taskId: string, ...rest: A) => Promise<boolean | void>,
  onRefused: () => void,
) {
  return async (taskId: string, ...rest: A): Promise<boolean | void> => {
    if (isDemoScenarioRescheduleRefused(taskId)) { onRefused(); return false; }
    return write(taskId, ...rest);
  };
}

/** True for a task in the confirmed walkthrough's manifest (never name-based). */
export const isDemoManifestTask = (taskId: string) => !!active && active.taskIds.has(taskId);
/** A confirmed guided walkthrough is running (undo/redo are blocked, history suppressed). */
export const isGuidedDemoActive = () => !!active;

export function gateDemoTaskWrite<T>(taskId: string, write: () => Promise<T>, refused: T, onRefused?: () => void): Promise<T> {
  const s = active;
  if (!s || !s.taskIds.has(taskId)) return write();
  if (!editsOpen) { onRefused?.(); return Promise.resolve(refused); }
  return track(Promise.resolve().then(write));
}

type Row = { approver_type?: string | null; approver_user_id?: string | null; approver_email?: string | null };
export type DemoEligibility =
  | { kind: 'none' }                       // not a walkthrough row: production rules apply
  | { kind: 'eligible'; slot: PersonaSlot }
  | { kind: 'refused'; message: string };

const SLOT_LABEL: Record<PersonaSlot, string> = { owner: 'the owner', admin: 'the admin', manager: 'the manager', user_1: 'the first team member', user_2: 'the second team member', visitor: 'the client' };

/** Shared review eligibility for synthetic walkthrough rows only. */
export function demoEligibility(row: Row, taskId: string, instanceId: string | null | undefined): DemoEligibility {
  const s = active;
  const slot = syntheticPersonaSlot(row);
  if (!s || !slot || instanceId !== s.instanceId || !s.taskIds.has(taskId)) return { kind: 'none' };
  const b = bound;
  if (!b || b.taskId !== taskId) return { kind: 'refused', message: 'This walkthrough review opens at its review chapter.' };
  if (slot !== b.reviewerSlot) return { kind: 'refused', message: `This review belongs to ${SLOT_LABEL[slot]}; this chapter's reviewer is ${SLOT_LABEL[b.reviewerSlot]}.` };
  if (b.activeSlot !== slot) return { kind: 'refused', message: `Switch to ${SLOT_LABEL[slot]}'s view to decide this review.` };
  return { kind: 'eligible', slot };
}

/** Returns the persona slot to route through the demo decision, or null (production path). */
export function demoDecisionRouteFor(row: Row, taskId: string, instanceId: string | null | undefined): PersonaSlot | null {
  const e = demoEligibility(row, taskId, instanceId);
  return e.kind === 'eligible' ? e.slot : null;
}

/**
 * Console queue visibility for one row. 'normal' = not a walkthrough row, the
 * production/Atlas actor filters decide. Synthetic walkthrough rows show only
 * when they are the bound checkpoint's row for the active persona.
 */
export function demoQueueVisibility(row: Row, taskId: string, instanceId: string | null | undefined): 'normal' | 'show' | 'hide' {
  const e = demoEligibility(row, taskId, instanceId);
  return e.kind === 'none' ? 'normal' : e.kind === 'eligible' ? 'show' : 'hide';
}

/** Synthetic walkthrough rows never get approval links or reminder emails. */
export const isSyntheticDemoRow = (row: Row) => syntheticPersonaSlot(row) !== null;

const MESSAGES: Record<string, string> = {
  not_authenticated: 'Your session expired. Sign in again, then retry.',
  not_operator: 'Only a platform architect can record walkthrough persona decisions.',
  not_member: 'You need active membership in the Demos workspace to record walkthrough decisions.',
  invalid_decision: 'That decision is not valid.',
  invalid_input: 'Your comments are too long. Shorten them and retry.',
  changes_required: 'List at least one change before requesting changes.',
  invalid_persona: 'That walkthrough persona is not valid.',
  wrong_persona: 'This persona is not a reviewer on this walkthrough task.',
  not_found: 'This review no longer exists.',
  chain_mismatch: 'This review is not part of the Demos walkthrough project.',
  not_scenario: 'This task is not part of a recognised walkthrough scenario.',
  not_demo_approval: 'Only synthetic walkthrough reviews can be decided here.',
  superseded: 'A newer review round replaced this one. Nothing was recorded.',
  already_responded: 'This review was already decided.',
  stale: 'The walkthrough moved on before this decision was saved. Nothing was accepted.',
  failed: 'We could not confirm the decision was saved. Refresh before trying again.',
};
export const demoDecisionMessage = (code: string) => MESSAGES[code] ?? MESSAGES.failed;

const OPEN = ['pending', 'notified'];

/**
 * No-migration Demos adapter. Uses authority the architect already has
 * (Managers-can-update RLS + field-lock architect branch); it adds no server
 * authority. Checks, in order: real signed-in id, live `is_architect`, active
 * confirmed session, exact project/task manifest, synthetic persona identity,
 * current round, open status; then ONE conditional row update, and a re-read.
 * Never writes tasks and never sends email. Does NOT validate ordinary-member
 * JWT authorization.
 */
type DecideArgs = {
  approvalId: string; taskId: string; personaSlot: PersonaSlot; decision: ApprovalDecision;
  comments?: string | null; changesNeeded?: string[] | null;
};
export function decideDemoWalkthroughApproval(args: DecideArgs): Promise<DecisionResult> {
  return track(decideInner(args));
}
async function decideInner(args: DecideArgs): Promise<DecisionResult> {
  const g = generation;
  const b = bound;
  const fail = (code: string, recordedStatus?: string): DecisionResult => ({ ok: false, code, message: demoDecisionMessage(code), recordedStatus });
  const s = active;
  if (!s || !s.taskIds.has(args.taskId)) return fail('chain_mismatch');
  if (!b || b.taskId !== args.taskId || b.reviewerSlot !== args.personaSlot || b.activeSlot !== args.personaSlot) return fail('wrong_persona');
  const live = () => generation === g && active === s && bound === b;
  if (args.decision !== 'approved' && args.decision !== 'rejected') return fail('invalid_decision');
  const changes = args.decision === 'rejected' ? (args.changesNeeded ?? []).map(c => c.trim()).filter(Boolean) : null;
  if (args.decision === 'rejected' && (!changes || changes.length === 0)) return fail('changes_required');
  const comments = args.comments?.trim() || null;
  if (comments && comments.length > 2000) return fail('invalid_input');
  if (!SLOTS.includes(args.personaSlot)) return fail('invalid_persona');

  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return fail('not_authenticated');
    const { data: isArch, error: archErr } = await supabase.rpc('is_architect', { _user_id: uid });
    if (archErr) return fail('failed');
    if (isArch !== true) return fail('not_operator');
    if (!live()) return fail('stale');

    const { data: rows, error } = await supabase.from('task_approvals')
      .select(DEMO_REVIEW_COLUMNS)
      .eq('task_id', args.taskId);
    if (error || !rows) return fail('failed');
    const row = rows.find(r => r.id === args.approvalId);
    if (!row) return fail('not_found');
    if (row.task_id !== args.taskId || row.project_id !== DEMOS_PROJECT) return fail('chain_mismatch');
    // Owned-id ledger replaces any token check: token columns are never readable client-side.
    if (!isOwnedReviewId(row.id) || syntheticPersonaSlot(row) !== args.personaSlot || row.scope !== 'task' || row.action_item_id) return fail('not_demo_approval');
    const newer = rows.some(o => o.id !== row.id && o.approver_email === row.approver_email
      && (o.created_at > row.created_at || (o.created_at === row.created_at && o.id > row.id)));
    if (newer) return fail('superseded');
    if (!OPEN.includes(row.status)) return fail('already_responded', row.status);

    // Recheck session/checkpoint generation immediately before the conditional write.
    if (!live()) return fail('stale');
    const { data: upd, error: updErr } = await supabase.from('task_approvals')
      .update({ status: args.decision, comments, changes_needed: changes, responded_at: new Date().toISOString() })
      .eq('id', row.id).eq('task_id', args.taskId).eq('project_id', DEMOS_PROJECT)
      .eq('approver_type', 'email').eq('approver_email', row.approver_email!).is('approver_user_id', null)
      .in('status', OPEN)
      .select('id, task_id, status, comments, changes_needed, responded_at, scope, action_item_id');
    if (updErr || !upd) return fail('failed');
    if (upd.length !== 1) {
      // No row matched: someone else decided first, or it changed. Report what is saved.
      const { data: now } = await supabase.from('task_approvals').select('status').eq('id', row.id).maybeSingle();
      return now && !OPEN.includes(now.status) ? fail('already_responded', now.status) : fail('failed');
    }
    const { data: saved, error: savedErr } = await supabase.from('task_approvals').select('id, status').eq('id', row.id).maybeSingle();
    if (savedErr || !saved || saved.status !== args.decision || upd[0].status !== args.decision) return fail('failed');
    if (!live()) return fail('stale');
    return { ok: true, approval: upd[0] as unknown as DecidedApproval };
  } catch {
    return fail('failed');
  }
}

/**
 * Theater path: decides only the bound checkpoint reviewer's own current-round
 * row for the persona being viewed. Returns null (production path) only when
 * the task has no synthetic walkthrough rows at all.
 */
export async function decideDemoTaskApprovalForTheater(args: {
  taskId: string; instanceId: string | null; decision: ApprovalDecision; comments?: string | null; changesNeeded?: string[] | null;
}): Promise<DecisionResult | null> {
  const s = active;
  if (!s || args.instanceId !== s.instanceId || !s.taskIds.has(args.taskId)) return null;
  // Capture session, generation and bound checkpoint BEFORE any await; the
  // whole operation (lookup + delegation) is tracked so exit/jump drains it.
  const g = generation;
  const b0 = bound;
  return track(theaterInner(args, s, g, b0));
}
async function theaterInner(
  args: { taskId: string; instanceId: string | null; decision: ApprovalDecision; comments?: string | null; changesNeeded?: string[] | null },
  s: ConfirmedDemoSession, g: number, b0: BoundDemoCheckpoint | null,
): Promise<DecisionResult | null> {
  const { data, error } = await supabase.from('task_approvals')
    .select(DEMO_REVIEW_COLUMNS)
    .eq('task_id', args.taskId);
  if (error || !data) return { ok: false, code: 'unavailable', message: 'Reviews could not be loaded. Nothing was decided.' };
  const synthetic = data.filter(r => syntheticPersonaSlot(r) && r.scope === 'task' && isOwnedReviewId(r.id));
  if (!synthetic.length) return null;
  // Walkthrough moved on during the lookup (jump, exit, new session): refuse.
  if (generation !== g || active !== s || bound !== b0) return { ok: false, code: 'stale', message: demoDecisionMessage('stale') };
  const b = b0;
  const refuse = (message: string): DecisionResult => ({ ok: false, code: 'wrong_persona', message });
  if (!b || b.taskId !== args.taskId) return refuse('This walkthrough review opens at its review chapter.');
  if (b.activeSlot !== b.reviewerSlot) return refuse(`Switch to ${SLOT_LABEL[b.reviewerSlot]}'s view to decide this review.`);
  let row: (typeof data)[number] | undefined;
  for (const r of synthetic) {
    if (syntheticPersonaSlot(r) !== b.reviewerSlot) continue;
    if (!row || r.created_at > row.created_at || (r.created_at === row.created_at && r.id > row.id)) row = r;
  }
  if (!row) return refuse(`There is no review for ${SLOT_LABEL[b.reviewerSlot]} on this task.`);
  if (row.status !== 'pending' && row.status !== 'notified') return { ok: false, code: 'already_responded', message: demoDecisionMessage('already_responded'), recordedStatus: row.status };
  return decideDemoWalkthroughApproval({ approvalId: row.id, taskId: args.taskId, personaSlot: b.reviewerSlot, ...args });
}

/**
 * Owner-health chapter scope: the single confirmed scenario root (a manifest
 * task with no parent). Returns null — never a name match or global pick —
 * when there is no confirmed session or the root is not exactly one task.
 */
export function resolveDemoHealthRoot(tasks: ReadonlyArray<{ id: string; parentId?: string | null }>): string | null {
  const s = active;
  if (!s) return null;
  const roots = tasks.filter(t => s.taskIds.has(t.id) && !t.parentId);
  return roots.length === 1 ? roots[0].id : null;
}
